import { useEffect, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  createGroupUserPortal,
  getDirectoryGroup,
  getUserPortalAllGroupTemplates,
  getUserPortalDirectoryGroup,
  getDirectoryGroupQuery,
} from '../../api/users-api';
import { handleErrors } from '../../utils/errors';
import { DirectoryGroup } from '../../../types';
import GeneralDialog from '../../shared/general-dialog';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStore } from '../state/zustand-store';
import { useAnalytics } from '../../shared/analytics/create-events';
import * as analytics from '../../shared/analytics/analytics.json';
import { ModuleList } from '../../shared/analytics/module-list';
import { useUserSettings } from '../../utils/use-user-settings';
import { Messages } from '../../utils/message-constants';
import { sortGroupTemplatesByName } from '../../shared/group-settings.config';
import { pagesConfig } from '../../shared/pages-config';
import { emailRegex } from '../../utils/regular-expressions';
import { userPortalPagesConfig } from '../../shared/user-portal-pages-config';

import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Close from '@mui/icons-material/Close';

interface AddGroupFormProps {
  open: boolean;
  handleClose: () => void;
  domains: string[];
}

export default function AddGroupDialog({
  open,
  handleClose,
  domains,
}: AddGroupFormProps) {
  const { primaryDomain } = useUserSettings();

  const { createEvent } = useAnalytics();
  const [domain, setDomain] = useState(
    domains.includes(primaryDomain) ? primaryDomain : domains[0]
  );
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const userView = location.pathname.includes(
    userPortalPagesConfig.userPortal.link
  );
  interface FormInput {
    name: string;
    email: string;
    description: string;
    groupTemplate: number | null;
    labels: { [key: string]: boolean };
  }

  async function checkIfGroupEmailExists(userEmail: string) {
    try {
      if (!userView) {
        await getDirectoryGroup(userEmail);
      } else {
        await getUserPortalDirectoryGroup(userEmail);
      }
      return true;
    } catch {
      return false;
    }
  }

  useEffect(() => {
    const domainToSet = domains.includes(primaryDomain)
      ? primaryDomain
      : domains[0];

    if (domain !== domainToSet) {
      setDomain(domainToSet);
    }
  }, [domains]);

  const { data: groupTemplates, isLoading: templateLoading } = useQuery({
    queryKey: [UserKeys.GroupTemplates],
    queryFn: () => getUserPortalAllGroupTemplates(),
    select: sortGroupTemplatesByName,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('group templates'),
    },
  });

  const {
    handleSubmit,
    control,
    reset,
    getValues,
    formState: { errors, isDirty, isValidating },
  } = useForm<FormInput>({
    mode: 'onBlur',
    defaultValues: {
      name: '',
      email: '',
      description: '',
      groupTemplate: null,
      labels: {
        isMailing: true, //always true
        isSecurity: false,
        isLocked: false,
      },
    },
  });

  const onSubmit = (data: FormInput) => {
    const createdGroup: DirectoryGroup = {
      name: data.name,
      email: data.email.toLowerCase() + '@' + domain,
      description: data.description,
    };
    userPortalMutate(createdGroup);
  };

  const { mutate: userPortalMutate, isPending: userPortalPending } =
    useMutation({
      mutationFn: (data: DirectoryGroup) => {
        const templateId = getValues('groupTemplate');

        return createGroupUserPortal(
          data,
          templateId ? templateId.toString() : null
        );
      },

      onSuccess: (data) => {
        setSnackbar({
          open: true,
          severity: 'success',
          message: Messages.Success.Created('Group'),
        });

        queryClient.setQueryData(
          getDirectoryGroupQuery(data.email, true).queryKey,
          {
            ...data,
            directMembersCount: 0,
          }
        );

        navigate(`${pagesConfig.groups.link}/${data.email}`);
        queryClient.invalidateQueries({
          queryKey: [UserKeys.Groups],
        });
        handleClose();
        reset();
      },
      onError: (error) => {
        handleErrors({
          error,
          snackbarMessage: Messages.Errors.Network.Creating('this group'),
        });
      },
      onSettled: () => {
        createEvent(analytics.userView.all_groups.create_group, {
          module: ModuleList.modules.userView.name,
          section: ModuleList.modules.userView.sections.allGroups,
        });
      },
    });

  return (
    <GeneralDialog
      open={open}
      maxWidth="md"
      handleClose={() => {
        reset();
        setDomain(domains.includes(primaryDomain) ? primaryDomain : domains[0]);
        handleClose();
      }}
      title="Create Group"
      dialogContent={
        <Stack sx={{ px: 6 }}>
          <Controller
            name={'name'}
            control={control}
            rules={{
              required: 'Name is required.',
            }}
            render={({ field }) => (
              <TextField
                label="Name"
                fullWidth
                autoComplete="off"
                error={!!errors.name}
                helperText={errors?.name?.message || ' '}
                required
                {...field}
              />
            )}
          />
          <Stack flexDirection="row" alignItems="baseline" width="100%" gap={1}>
            <Controller
              name={'email'}
              control={control}
              rules={{
                required: 'Email is required.',
                validate: {
                  userNameExists: async (value) => {
                    const domain =
                      primaryDomain.length !== 0 ? primaryDomain : domains[0];
                    const userEmail = `${value}@${domain}`;
                    const userExists = await checkIfGroupEmailExists(userEmail);

                    if (userExists) {
                      return 'Group already exists.';
                    }
                  },
                  notValidEmail: (value) => {
                    const domain =
                      primaryDomain.length !== 0 ? primaryDomain : domains[0];
                    const userEmail = `${value}@${domain}`;
                    if (value && !emailRegex.test(userEmail)) {
                      return `${userEmail} is not a valid email address.`;
                    }
                    return true;
                  },
                },
              }}
              render={({ field }) => (
                <TextField
                  label="Email"
                  fullWidth
                  autoComplete="off"
                  error={!!errors.email}
                  helperText={errors?.email?.message || ' '}
                  required
                  {...field}
                  slotProps={{
                    input: {
                      endAdornment: isValidating ? (
                        <CircularProgress size={20} />
                      ) : null,
                    },
                  }}
                />
              )}
            />
            <Typography>@</Typography>
            <TextField
              label="Domain"
              select
              fullWidth
              value={domain}
              onChange={(event) => setDomain(event.target.value)}
            >
              {domains.map((domain) => (
                <MenuItem value={domain} key={domain}>
                  {domain}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <Stack gap={2} mt={1}>
            <Controller
              name={'description'}
              control={control}
              render={({ field }) => (
                <TextField
                  label="Description"
                  fullWidth
                  autoComplete="off"
                  {...field}
                />
              )}
            />
            {templateLoading ? (
              <Skeleton variant="text" height={60} sx={{ fontSize: '1rem' }} />
            ) : (
              <Controller
                name={'groupTemplate'}
                control={control}
                render={({ field }) => {
                  const handleTemplateChange = (
                    newValue: number | string | null
                  ) => {
                    field.onChange(newValue ? Number(newValue) : null);
                  };
                  return (
                    <TextField
                      label={
                        groupTemplates?.length === 0
                          ? 'No group templates available'
                          : 'Group Template'
                      }
                      select
                      fullWidth
                      disabled={groupTemplates?.length === 0}
                      value={field.value || ''}
                      onChange={(e) => handleTemplateChange(e.target.value)}
                      slotProps={{
                        input: {
                          endAdornment: field.value && (
                            <InputAdornment position="end" sx={{ mr: 2.5 }}>
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTemplateChange(null);
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                <Close />
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }}
                    >
                      {groupTemplates?.map((template) => (
                        <MenuItem value={template.id} key={template.id}>
                          {template.groupTemplateName}
                        </MenuItem>
                      ))}
                    </TextField>
                  );
                }}
              />
            )}
          </Stack>
        </Stack>
      }
      primaryButton={{
        loading: userPortalPending,
        action: handleSubmit(onSubmit),
        disabled: !isDirty || userPortalPending,
        title: 'Add',
      }}
    />
  );
}
